import React from 'react';
import {
  SelectedWeekRange,
  AllowedWeekRange,
} from 'src/pages/Hindsighting/StyleColorReview/CanvasView/CanvasView.slice';
import { WeekRange } from 'src/common-ui/components/WeekRange/WeekRangePicker.interface';
import { getCurrentAndPastDatesFromRange } from 'src/components/AssortmentScopeSelector/AssortmentPeriodForm/AssortmentPeriodForm';
import WeekRangePicker from 'src/common-ui/components/WeekRange/WeekRangePicker';
import { size, mapValues, merge, isNil } from 'lodash';
import { Getter, Lens } from 'monocle-ts';
import { update } from 'src/services/lenses/Lenses.actions';
import { connect } from 'react-redux';
import { TEAL_PRIMARY } from 'src/utils/Style/Theme';
import { DaysRangeListResponse } from 'src/types/Scope';
import { AppState, AppThunkDispatch } from 'src/store';
import { AssortmentAddSlice } from './AssortmentAdd.slice';
import { AssortmentScopeSelectorSlice } from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { Tooltip } from '@material-ui/core';

type AssortmentAddRangePickerProps = AssortmentScopeSelectorSlice &
  SelectedWeekRange &
  AllowedWeekRange & {
    isAssortment: boolean;
    allowToggle: boolean;
  };
type DispatchProps = {
  weeksChanged: (range: SelectedWeekRange) => void;
  isAssortmentChanged: (newIsAssortment: boolean) => void;
};
type AllProps = AssortmentAddRangePickerProps & OwnProps & DispatchProps;
class AssortmentAddRangePicker extends React.Component<AllProps> {
  constructor(props: AllProps) {
    super(props);
  }

  onNewWeeksSelected = (range: WeekRange) => {
    const { daysRangeList, daysPastRangeList } = this.props;
    const mergedRangeList = (mapValues(daysRangeList, (list: Record<string, any>, key: keyof DaysRangeListResponse) => {
      return merge(list, daysPastRangeList[key]);
    }) as unknown) as DaysRangeListResponse;
    const dates = getCurrentAndPastDatesFromRange(range, mergedRangeList, daysPastRangeList);
    this.props.weeksChanged({
      from: dates.start || dates.historyStart,
      to: dates.end || dates.historyEnd,
    });
  };

  render() {
    const {
      rangeList,
      pastRangeList,
      daysRangeList,
      daysPastRangeList,
      from,
      to,
      isAssortment,
      allowFrom,
      allowTo,
    } = this.props;
    const actRangeList = !isAssortment ? pastRangeList : rangeList;
    const actDaysRangeList = !isAssortment ? daysPastRangeList : daysRangeList;
    if (size(actRangeList) <= 0 || size(actDaysRangeList) <= 0) {
      return <div />;
    }
    let labelStyle: Record<string, any>;
    if (this.props.allowToggle) {
      labelStyle = { color: TEAL_PRIMARY, cursor: 'pointer' };
    } else {
      labelStyle = {};
    }
    return (
      <div className="asst-add-range-picker" style={{ width: 170 }}>
        <div style={{ fontSize: '9pt' }}>
          Search in&nbsp;
          <Tooltip
            title={
              isAssortment
                ? 'Viewing items in Assortment, click to view Hindsighting items'
                : 'Viewing items in history, click to view Assortment items'
            }
          >
            <span
              style={labelStyle}
              onClick={() => this.props.allowToggle && this.props.isAssortmentChanged(!this.props.isAssortment)}
            >
              {isAssortment ? 'Assortment' : 'History'}
            </span>
          </Tooltip>
          :
        </div>
        <div>
          <WeekRangePicker
            autoSelect={true}
            allowFrom={allowFrom}
            allowTo={allowTo}
            dateToNameMap={actDaysRangeList}
            initialSelectedTo={to}
            initialSelectedFrom={from}
            numberOfMonths={2}
            onNewWeeksSelected={this.onNewWeeksSelected}
            selectionOptions={[{ label: 'Start' }, { label: 'End' }]}
          />
        </div>
      </div>
    );
  }
}

export interface OwnProps {
  scopeGetter: Getter<AppState, AssortmentScopeSelectorSlice>;
  addStylesSlice: Lens<AppState, AssortmentAddSlice>;
  assortmentModel?: string;
}

function mapStateToProps(state: AppState, ownProps: OwnProps): AssortmentAddRangePickerProps {
  const { scopeRange, assortmentRange } = ownProps.addStylesSlice.get(state);
  const scope = ownProps.scopeGetter.get(state);
  const {
    pastRangeList,
    rangeList: assortmentRangeList,
    daysPastRangeList,
    daysRangeList: daysAssortmentRangeList,
  } = scope;
  const rangeList = assortmentRange ? assortmentRangeList : pastRangeList;
  const daysRangeList = assortmentRange ? daysAssortmentRangeList : daysPastRangeList;

  let allowFrom = '';
  let allowTo = '';
  if (Array.isArray(rangeList) && rangeList.length > 0) {
    // Makes sure values are in both rangeList and daysRangeList
    const startRangeList = rangeList.filter((x) => Object.values(daysRangeList.start_date).indexOf(x.id) !== -1);
    const endRangeList = rangeList.filter((x) => Object.values(daysRangeList.end_date).indexOf(x.id) !== -1);
    allowFrom = startRangeList[0].id;
    allowTo = endRangeList[endRangeList.length - 1].id;
  }

  const allowToggle = !isNil(ownProps.assortmentModel);
  let range: SelectedWeekRange;
  if (scopeRange) {
    range = scopeRange;
  } else {
    const historyStart = scope.scope.historyStart || '';
    const historyEnd = scope.scope.historyEnd || '';
    range = {
      from: assortmentRange && scope.scope.start ? scope.scope.start : historyStart,
      to: assortmentRange && scope.scope.end ? scope.scope.end : historyEnd,
    };
  }
  const rangePickerProps: AssortmentAddRangePickerProps = {
    ...ownProps.scopeGetter.get(state),
    ...range,
    isAssortment: !!assortmentRange,
    allowToggle,
    allowFrom,
    allowTo,
  };
  return rangePickerProps;
}

function mapDispatchToProps(dispatch: AppThunkDispatch, ownProps: OwnProps): DispatchProps {
  return {
    isAssortmentChanged: (newIsAssortment) => {
      dispatch((_ign: AppThunkDispatch, getState: () => AppState) => {
        const { viewDefn } = ownProps.addStylesSlice.get(getState());
        const scope = ownProps.scopeGetter.get(getState());
        let newModelDefn: string | undefined;
        let range: SelectedWeekRange | undefined;
        if (newIsAssortment && scope.scope.start && scope.scope.end) {
          newModelDefn = viewDefn.assortmentModel || '';
          range = {
            from: scope.scope.start,
            to: scope.scope.end,
          };
        } else if (scope.scope.historyStart && scope.scope.historyEnd) {
          newModelDefn = viewDefn.model || viewDefn.assortmentModel || '';
          range = {
            from: scope.scope.historyStart,
            to: scope.scope.historyEnd,
          };
        }

        // action will be captured by AssortmentAddView epic and trigger data reload
        dispatch(
          update(
            ownProps.addStylesSlice
              .compose(Lens.fromProps<AssortmentAddSlice>()(['scopeRange', 'assortmentRange', 'modelDefn']))
              .set({
                scopeRange: range,
                assortmentRange: newIsAssortment,
                modelDefn: newModelDefn,
              }),
            'ASST_ADD_ASST_CHANGED',
          ),
        );
      });
    },
    weeksChanged: (range) => {
      // action will be captured by AssortmentAddView epic and trigger data reload
      dispatch(
        update(
          ownProps.addStylesSlice.compose(Lens.fromProp<AssortmentAddSlice>()('scopeRange')).set(range),
          'ASST_ADD_WEEKS_CHANGED',
        ),
      );
    },
  };
}

// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(AssortmentAddRangePicker);
